/* eslint-disable no-console */
import { useState, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Menu, Tooltip, Avatar, Dropdown } from 'antd';
import { DownOutlined, KeyOutlined, LogoutOutlined, MailOutlined } from '@ant-design/icons';
import {
  Users,
  FileText,
  ShoppingBag,
  Camera,
  MessageSquare,
  CreditCard,
  BarChart3,
  Tag,
  Home,
  Image,
  Briefcase,
  Package,
  Globe,
  Search,
  History,
  Shield,
  UserPlus,
  UserCheck,
  Users2,
  Share2,
  Settings,
  DollarSign,
  Receipt,
  Wallet,
  ArrowLeftRight,
  FileCheck,
  FileX,
  Star,
  ListChecks,
  Percent,
  Image as ImageIcon,
  Newspaper,
  Lightbulb,
  HelpCircle,
  Camera as CameraIcon,
  Ruler,
  FileType,
  CreditCard as CreditCardIcon,
  Layers,
  LayoutGrid,
  FolderKanban,
  HelpCircle as HelpCircleIcon,
  FileText as FileTextIcon,
  CheckSquare,
  Archive,
  ClipboardList,
  AlignLeft,
  BookOpen,
  FileSpreadsheet,
  Phone,
  PanelLeftClose,
} from 'lucide-react';

const { SubMenu } = Menu;

import { AppRoutes, RemoveRolePermission, Role, entityArray } from '../../constants/AppConstant';
import {
  getRole,
  getAdminEmail,
  getAdminName,
  getEmployeeId,
  deleteToken,
} from '../../service/CookieManager';

import './styles.scss';

const iconMap: Record<string, any> = {
  'item-0': LayoutGrid,
  'item-1': Users,
  'item-1-1': Shield,
  'item-1-2': Users2,
  'item-1-3': UserCheck,
  'item-1-4': UserPlus,
  'item-1-5': Share2,
  'item-1-6': Settings,
  'item-2': FileText,
  'item-3': ShoppingBag,
  'item-4': Camera,
  'item-13': MessageSquare,
  'item-13-1': Briefcase,
  'item-13-2': MessageSquare,
  'item-5': CreditCard,
  'item-5-1': Camera,
  'item-5-2': DollarSign,
  'item-5-3': Receipt,
  'item-5-4': Wallet,
  'item-5-5': ArrowLeftRight,
  'item-5-6': FileCheck,
  'item-5-7': FileX,
  'item-6': BarChart3,
  'item-6-1': FileSpreadsheet,
  'item-6-2': FileSpreadsheet,
  'item-7': Tag,
  'item-8': Home,
  'item-8-1': HelpCircle,
  'item-8-2': Lightbulb,
  'item-8-3': Star,
  'item-8-4': ListChecks,
  'item-8-5': Percent,
  'item-8-6': ImageIcon,
  'item-8-7': Newspaper,
  'item-8-8': BookOpen,
  'item-9': Image,
  'item-9-1': Lightbulb,
  'item-9-2': ListChecks,
  'item-9-3': CameraIcon,
  'item-9-4': HelpCircle,
  'item-9-5': Ruler,
  'item-10': Briefcase,
  'item-10-1': FolderKanban,
  'item-10-2': Layers,
  'item-10-3': AlignLeft,
  'item-10-4': LayoutGrid,
  'item-10-5': Image,
  'item-10-5-1': Ruler,
  'item-10-5-2': FileType,
  'item-10-5-3': CreditCardIcon,
  'item-10-6': Layers,
  'item-10-6-1': LayoutGrid,
  'item-10-6-2': FolderKanban,
  'item-10-6-3': Layers,
  'item-10-6-4': HelpCircleIcon,
  'item-11': Package,
  'item-11-1': Archive,
  'item-11-2': CheckSquare,
  'item-11-3': ClipboardList,
  'item-11-4': HelpCircleIcon,
  'item-11-5': FileTextIcon,
  'item-11-6': AlignLeft,
  'item-12': Globe,
  'item-12-1': FileText,
  'item-12-2': FileTextIcon,
  'item-12-3': Users,
  'item-12-4': Phone,
  'item-14': Search,
  'item-15': History,
};

const defaultIcon = FileText;

const getIcon = (key: string, size = 18) => {
  const Icon = iconMap[key] || defaultIcon;
  return <Icon size={size} strokeWidth={2} />;
};

const getKeys = (url: string, entityArr: any = entityArray): string[] => {
  for (const entityObject of entityArr) {
    if (entityObject.entityUrl === url) {
      return [entityObject.key];
    }
    if (entityObject.children) {
      const nested = getKeys(url, entityObject.children);
      if (nested.length) {
        return [entityObject.key, ...nested];
      }
    }
  }
  return [];
};

const findTopLevelKeys = (items: any[]): string[] => {
  return items.map(item => item.key);
};

const renderTooltipNavItems = (items: any[], level = 0): JSX.Element[] =>
  items.map((item: any) => {
    const hasChildren = !!item.children && item.children.length > 0;
    const hasUrl = !!item.entityUrl;

    return (
      <div key={item.key} className={`sidebar-tooltip-row ${hasChildren ? 'has-children' : ''}`}>
        {hasUrl ? (
          <Link
            to={item.entityUrl}
            className={`sidebar-tooltip-item ${hasChildren ? 'has-children' : ''}`}
          >
            <span className="sidebar-tooltip-item-icon">{getIcon(item.key, 15)}</span>
            <span className="sidebar-tooltip-item-label">{item.entityName}</span>
            {hasChildren && (
              <span className="sidebar-tooltip-item-caret" aria-hidden>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </span>
            )}
          </Link>
        ) : (
          <div
            className={`sidebar-tooltip-item sidebar-tooltip-item-pseudo ${hasChildren ? 'has-children' : ''}`}
          >
            <span className="sidebar-tooltip-item-icon">{getIcon(item.key, 15)}</span>
            <span className="sidebar-tooltip-item-label">{item.entityName}</span>
            {hasChildren && (
              <span className="sidebar-tooltip-item-caret" aria-hidden>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </span>
            )}
          </div>
        )}
        {hasChildren && (
          <div className={`sidebar-tooltip-submenu level-${level}`}>
            {renderTooltipNavItems(item.children, level + 1)}
          </div>
        )}
      </div>
    );
  });

const renderNavItems = (
  items: any[],
  disableModuleList: string[] | undefined,
  hasSpfManagementAccess: boolean,
  restrictedSpfRoutes: string[],
  onTitleClick: (e: any) => void,
  collapsed: boolean,
  isTopLevel = false
): any[] =>
  items
    .filter((item: any) => {
      if (isTopLevel) {
        const isDisabled = disableModuleList?.includes(item.entityName);
        if (!isDisabled) return true;
        return (
          hasSpfManagementAccess &&
          (item.entityName === 'Services' || item.entityName === 'Packages')
        );
      }
      return true;
    })
    .map((item: any) => {
      if (item.children && item.children.length) {
        const parentDisabled = !!disableModuleList?.includes(item.entityName);
        const visibleChildren = item.children.filter(
          (child: any) =>
            !disableModuleList?.includes(child.key) &&
            !disableModuleList?.includes(child.entityName) &&
            (!parentDisabled || restrictedSpfRoutes.includes(child.entityUrl)) &&
            (hasSpfManagementAccess || !restrictedSpfRoutes.includes(child.entityUrl))
        );
        if (visibleChildren.length === 0) return null;

        const titleContent = (
          <span className="sidebar-menu-title">
            <span className="sidebar-menu-icon">{getIcon(item.key)}</span>
            {!collapsed && <span className="sidebar-menu-text">{item.entityName}</span>}
          </span>
        );

        if (collapsed && isTopLevel) {
          return (
            <Menu.Item key={item.key} className="sidebar-collapsed-has-children">
              <Tooltip
                placement="right"
                overlayClassName="sidebar-tooltip"
                title={
                  <div className="sidebar-tooltip-content">
                    <div className="sidebar-tooltip-title">{item.entityName}</div>
                    <div className="sidebar-tooltip-items">
                      {renderTooltipNavItems(visibleChildren, 0)}
                    </div>
                  </div>
                }
              >
                <span className="sidebar-menu-icon-badge">
                  <span className="sidebar-menu-icon-only">{getIcon(item.key, 18)}</span>
                </span>
              </Tooltip>
            </Menu.Item>
          );
        }

        return (
          <SubMenu key={item.key} title={titleContent} onTitleClick={onTitleClick} icon={null}>
            {renderNavItems(
              visibleChildren,
              disableModuleList,
              hasSpfManagementAccess,
              restrictedSpfRoutes,
              onTitleClick,
              collapsed
            )}
          </SubMenu>
        );
      }

      if (collapsed) {
        return (
          <Menu.Item key={item.key}>
            <Tooltip
              placement="right"
              overlayClassName="sidebar-tooltip"
              title={
                <div className="sidebar-tooltip-content">
                  <div className="sidebar-tooltip-items">
                    {renderTooltipNavItems([item], 0)}
                  </div>
                </div>
              }
            >
              <span
                className="sidebar-menu-icon-badge"
                aria-label={item.entityName}
              >
                <span className="sidebar-menu-icon-only">{getIcon(item.key, 18)}</span>
              </span>
            </Tooltip>
          </Menu.Item>
        );
      }

      const linkContent = (
        <>
          <span className="sidebar-menu-icon">{getIcon(item.key)}</span>
          <span className="sidebar-menu-text">{item.entityName}</span>
        </>
      );

      return (
        <Menu.Item key={item.key}>
          <Link to={item.entityUrl} className="sidebar-menu-link">
            {linkContent}
          </Link>
        </Menu.Item>
      );
    });

export type EntityArray = {
  key: string;
  entityName: string;
  entityUrl: string;
  moduleName: string;
  children?: EntityArray[];
};

interface SidebarProps {
  title: string;
  logo?: string;
  entityArr: EntityArray[];
}

const Sidebar = (props: SidebarProps) => {
  const { entityArr } = props;
  const history = useHistory();

  const currentUserRole: any = getRole();
  const rolePermission = RemoveRolePermission[currentUserRole];
  const disableModuleList = rolePermission?.disableModule;
  const hasSpfManagementAccess =
    currentUserRole === Role.SUPER_ADMIN || currentUserRole === Role.CONTENT_MANAGER;
  const restrictedSpfRoutes: string[] = [
    AppRoutes.ServiceProcessFaqManagement,
    AppRoutes.PackageProcessFaqManagement,
    AppRoutes.ServiceContentManagement,
    AppRoutes.PackageContentManagement,
    AppRoutes.ContactServiceContentManagement,
    AppRoutes.ContactPackageContentManagement,
    AppRoutes.RelatedArticle,
  ];

  const employeeId = getEmployeeId() || '—';
  const adminName = getAdminName() || '—';
  const email = getAdminEmail() || '—';
  const role = getRole() || '—';
  const initials =
    adminName !== '—'
      ? adminName
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map(namePart => namePart[0])
          .join('')
          .toUpperCase()
      : 'DM';

  const profileMenu = (
    <Menu className="cms-profile-menu">
      <Menu.Item key="profile" disabled className="cms-profile-menu-header">
        <div className="cms-profile-menu-header-inner">
          <Avatar size={48} className="cms-profile-menu-header-avatar">
            {initials}
          </Avatar>
          <div className="cms-profile-menu-header-text">
            <strong className="cms-profile-menu-name">{adminName}</strong>
            <div className="cms-profile-menu-meta">
              <span className="cms-profile-menu-role">{role}</span>
              <span className="cms-profile-menu-id">{employeeId}</span>
            </div>
          </div>
        </div>
        <div className="cms-profile-menu-email">
          <div className="cms-profile-menu-email-icon-box">
            <MailOutlined className="cms-profile-menu-email-icon" aria-hidden />
          </div>
          <span className="cms-profile-menu-email-text">{email}</span>
        </div>
      </Menu.Item>
      <Menu.Divider className="cms-profile-menu-divider" />
      <Menu.Item
        key="change-password"
        className="cms-profile-menu-action"
        onClick={() => history.push(AppRoutes.ChangePassword)}
      >
        <div className="cms-profile-menu-action-row">
          <span className="cms-profile-menu-action-icon-box">
            <KeyOutlined className="cms-profile-menu-action-icon" />
          </span>
          <span className="cms-profile-menu-action-text">Change Password</span>
        </div>
      </Menu.Item>
      <Menu.Item
        key="logout"
        className="cms-profile-menu-action cms-profile-logout"
        onClick={() => {
          deleteToken();
          window.location.reload();
        }}
      >
        <div className="cms-profile-menu-action-row">
          <span className="cms-profile-menu-action-icon-box">
            <LogoutOutlined className="cms-profile-menu-action-icon" />
          </span>
          <span className="cms-profile-menu-action-text">Logout</span>
        </div>
      </Menu.Item>
    </Menu>
  );

  const [activeKey, setActiveKey] = useState<string>(getKeys('/', entityArr).slice(-1)[0] || '');
  const [openKey, setOpenKey] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const { pathname } = useLocation();

  const topLevelKeys = findTopLevelKeys(entityArr);

  const onItemSelect = (event: { keyPath: string[] }) => {
    const ancestors = event.keyPath.slice(1);
    setOpenKey(ancestors);
  };

  const onTitleClick = (event: { key: string }) => {
    const { key } = event;
    setOpenKey(prev => {
      const isOpen = prev.includes(key);
      if (isOpen) {
        return prev.filter(k => k !== key);
      }
      const isTopLevel = topLevelKeys.includes(key);
      if (isTopLevel) {
        const otherTopLevelOpen = prev.filter(k => !topLevelKeys.includes(k));
        return [...otherTopLevelOpen, key];
      }
      return [...prev, key];
    });
  };

  useEffect(() => {
    const keyPath = getKeys(pathname, entityArr);

    if (keyPath.length) {
      setActiveKey(keyPath[keyPath.length - 1]);
      setOpenKey(prev => {
        const newOpenKeys = [...prev];
        keyPath.slice(0, -1).forEach(key => {
          if (!newOpenKeys.includes(key)) {
            newOpenKeys.push(key);
          }
        });
        return newOpenKeys;
      });
    }
  }, [pathname]);

  return (
    <div className={`sidebar-holder ${collapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}>
      <div
        className="sidebar-header"
        role="button"
        tabIndex={0}
        onClick={() => setCollapsed(c => !c)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed(c => !c);
          }
        }}
      >
        {collapsed ? (
          <img
            style={{ backgroundColor: 'white', padding: '0 1px', borderRadius: '4px' }}
            src="/logoicon.png"
            alt="Documitra"
            className="sidebar-logo-icon"
          />
        ) : (
          <>
            <img
              src="/logowithname.png"
              alt="Documitra"
              style={{ backgroundColor: 'white', padding: '0 5px', borderRadius: '4px' }}
              className="sidebar-logo-full"
            />
            <button
              type="button"
              aria-label="Collapse sidebar"
              onClick={event => {
                event.stopPropagation();
                setCollapsed(true);
              }}
              style={{
                alignItems: 'center',
                background: 'transparent',
                border: 0,
                color: '#58709b',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                marginLeft: 'auto',
                padding: 6,
              }}
            >
              <PanelLeftClose size={20} strokeWidth={2} />
            </button>
          </>
        )}
      </div>

      <div className={`sidebar-navigation ${collapsed ? 'is-collapsed' : ''}`}>
        <Menu
          mode="inline"
          activeKey={activeKey}
          openKeys={collapsed ? [] : openKey}
          onSelect={onItemSelect}
          theme="dark"
        >
          {renderNavItems(
            entityArr,
            disableModuleList,
            hasSpfManagementAccess,
            restrictedSpfRoutes,
            onTitleClick,
            collapsed,
            true
          )}
        </Menu>
      </div>

      <div className="sidebar-profile-section">
        <Dropdown
          overlay={profileMenu}
          trigger={['click']}
          placement="topRight"
          destroyPopupOnHide
          getPopupContainer={() => document.body}
          overlayClassName="cms-profile-dropdown sidebar-profile-dropdown"
        >
          <div
            className={`sidebar-profile-trigger ${collapsed ? 'is-collapsed' : ''}`}
            role="button"
            tabIndex={0}
          >
            <Avatar size={40} className="sidebar-profile-avatar">
              {initials}
            </Avatar>
            {!collapsed && (
              <div className="sidebar-profile-summary" title={`${adminName} · ${employeeId}`}>
                <strong className="sidebar-profile-summary-name">{adminName}</strong>
                <span className="sidebar-profile-summary-id">{employeeId}</span>
              </div>
            )}
            {!collapsed && <DownOutlined className="sidebar-profile-trigger-chevron" />}
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

export default Sidebar;
